import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { GroupMemberPicker, MAX_GROUP_SIZE, MIN_GROUP_SIZE } from "../../../patient";
import { useCenterMembers, useCenterPatients, useCourseLabels, useCreateCenterGroup } from "../../../center";
import { STUDENT_ERRORS } from "../refusals";

const SELECT_CLASS =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700";

/** The most students of one mentor offered at once. */
const CANDIDATES_LIMIT = 500;

/**
 * A group of students the centre already has, under one of its mentors.
 *
 * As a therapist puts a group together from their own patients: nobody new is
 * taken on here. A group is one mentor's, so the mentor comes first and only
 * their students are offered.
 */
export default function GroupFromStudentsForm({ centerId, onClose }: { centerId: string; onClose: () => void }) {
    const { t } = useTranslation();
    const { members } = useCenterMembers(centerId, true);
    const mentors = members.filter((member) => member.roles.includes("therapist"));
    const create = useCreateCenterGroup(centerId);

    const [mentorId, setMentorId] = useState("");
    const [name, setName] = useState("");
    const [memberIds, setMemberIds] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();

    const { patients, loading } = useCenterPatients(mentorId ? centerId : undefined, {
        therapist_id: mentorId || undefined,
        limit: CANDIDATES_LIMIT,
    });
    const nameOf = (id: string) => {
        const patient = patients.find((p) => p.patient_id === id);
        return patient ? `${patient.first_name} ${patient.last_name}` : "";
    };
    const candidates = patients
        .filter((patient) => !patient.is_anonymous && !memberIds.includes(patient.patient_id))
        .map((patient) => ({
            value: patient.patient_id,
            label: `${labelOf(patient)}${patient.contact_email ? ` · ${patient.contact_email}` : ""}`,
        }));

    const sizeOk = memberIds.length >= MIN_GROUP_SIZE && memberIds.length <= MAX_GROUP_SIZE;
    const invalid = !mentorId || !sizeOk;

    const handleSubmit = async () => {
        setSubmitted(true);
        setError(null);
        setDone(false);
        if (invalid) return;
        try {
            await create.mutateAsync({
                therapist_id: mentorId,
                patient_ids: memberIds,
                display_name: name.trim() || null,
            });
            setDone(true);
            setMemberIds([]);
            setName("");
            setSubmitted(false);
        } catch (err: unknown) {
            const code = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setError(t(STUDENT_ERRORS[code ?? ""] ?? "center.students.errors.generic"));
        }
    };

    return (
        <form
            noValidate
            className="flex flex-col gap-5"
            onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
            }}
        >
            <p className="text-sm text-content-tertiary">{t("center.students.groupHint")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <Label htmlFor="group-mentor">
                        {t("center.students.mentor")}
                        <span className="text-red-500"> *</span>
                    </Label>
                    <select
                        id="group-mentor"
                        value={mentorId}
                        onChange={(e) => {
                            setMentorId(e.target.value);
                            // Another mentor's students cannot join their group.
                            setMemberIds([]);
                        }}
                        aria-invalid={submitted && !mentorId}
                        className={`${SELECT_CLASS} ${submitted && !mentorId ? "border-red-500" : ""}`}
                    >
                        <option value="">{t("center.students.chooseMentor")}</option>
                        {mentors.map((mentor) => (
                            <option key={mentor.therapist_id} value={mentor.therapist_id}>
                                {`${mentor.first_name} ${mentor.last_name}`}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label htmlFor="group-name">{t("center.students.groupNameOptional")}</Label>
                    <Input
                        id="group-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("center.students.groupNamePlaceholder")}
                    />
                </div>
            </div>

            {mentorId ? (
                <GroupMemberPicker
                    members={memberIds.map((id) => ({ id, name: nameOf(id) }))}
                    candidates={candidates}
                    loading={loading}
                    keepMinimum={false}
                    onAdd={(id) => setMemberIds((current) => [...current, id])}
                    onRemove={(member) => setMemberIds((current) => current.filter((id) => id !== member.id))}
                />
            ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t("center.students.chooseMentorFirst")}</p>
            )}

            {submitted && invalid && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("center.students.groupErrorForm", { min: MIN_GROUP_SIZE, max: MAX_GROUP_SIZE })}
                </p>
            )}
            {error && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            {done && (
                <p role="status" className="text-sm text-brand-700 dark:text-brand-300">
                    {t("center.students.groupCreated")}
                </p>
            )}

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>
                    {t("common.close")}
                </Button>
                <Button type="submit" loading={create.isPending} disabled={create.isPending}>
                    {t("center.students.submitGroup")}
                </Button>
            </div>
        </form>
    );
}
